/**
 * Brand Theme Injection Utility
 *
 * Generates perceptually-even color ramps from a base hex color using OKLCH
 * and injects them as CSS custom properties via a <style id="brand-theme"> element.
 *
 * Handles both dark (:root) and light (html.light) theme variants.
 * Idempotent -- safe to call multiple times (updates same style element).
 *
 * Exports:
 *   - generateColorScale(hex) -> Record<string, string> with steps 50..950
 *   - applyBrandTheme(primaryHex, secondaryHex) -> void
 *   - applyBranding(branding) -> void (full orchestrator: theme + title + favicon + localStorage)
 */

import type { BrandingPublicShape } from '@/config/brandingDefaults';

// --------------------------------------------------------------------------
// OKLCH color math (no external dependency -- hand-rolled for bundle size)
// --------------------------------------------------------------------------

interface OklchColor {
  l: number; // lightness 0..1
  c: number; // chroma 0..~0.4
  h: number; // hue 0..360
}

/**
 * Parse a #rrggbb hex string into linear sRGB [0..1].
 */
function hexToLinearRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  // sRGB -> linear
  const toLinear = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return [toLinear(r), toLinear(g), toLinear(b)];
}

/**
 * Linear sRGB -> OKLab.
 * Using the Bjorn Ottosson matrix method.
 */
function linearRgbToOklab(rgb: [number, number, number]): [number, number, number] {
  const [r, g, b] = rgb;

  const l_ = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m_ = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s_ = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ];
}

/**
 * OKLab -> OKLCH.
 */
function oklabToOklch(lab: [number, number, number]): OklchColor {
  const [l, a, b] = lab;
  const c = Math.sqrt(a * a + b * b);
  let h = (Math.atan2(b, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l, c, h };
}

/**
 * OKLCH -> OKLab.
 */
function oklchToOklab(color: OklchColor): [number, number, number] {
  const { l, c, h } = color;
  const hRad = (h * Math.PI) / 180;
  return [l, c * Math.cos(hRad), c * Math.sin(hRad)];
}

/**
 * OKLab -> linear sRGB.
 */
function oklabToLinearRgb(lab: [number, number, number]): [number, number, number] {
  const [L, a, b] = lab;

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  return [r, g, bl];
}

/**
 * Linear sRGB -> sRGB gamma-corrected [0..255].
 */
function linearToSrgb(c: number): number {
  const clamped = Math.max(0, Math.min(1, c));
  const gamma =
    clamped <= 0.0031308
      ? clamped * 12.92
      : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
  return Math.round(gamma * 255);
}

/**
 * OKLCH color -> hex string.
 */
function oklchToHex(color: OklchColor): string {
  const lab = oklchToOklab(color);
  const [r, g, b] = oklabToLinearRgb(lab);
  const sr = linearToSrgb(r);
  const sg = linearToSrgb(g);
  const sb = linearToSrgb(b);
  return `#${sr.toString(16).padStart(2, '0')}${sg.toString(16).padStart(2, '0')}${sb.toString(16).padStart(2, '0')}`;
}

/**
 * Convert hex to OKLCH.
 */
function hexToOklch(hex: string): OklchColor {
  const linear = hexToLinearRgb(hex);
  const lab = linearRgbToOklab(linear);
  return oklabToOklch(lab);
}

// --------------------------------------------------------------------------
// Color scale generation
// --------------------------------------------------------------------------

/**
 * Fixed lightness curve for generating 11 steps (50..950).
 * Step 500 maps to the base color (approximately -- we fix it exactly).
 * Dark theme: light to dark top-down.
 */
const LIGHTNESS_CURVE: Record<string, number> = {
  '50': 0.97,
  '100': 0.93,
  '200': 0.87,
  '300': 0.79,
  '400': 0.70,
  '500': 0.60,
  '600': 0.52,
  '700': 0.44,
  '800': 0.37,
  '900': 0.30,
  '950': 0.22,
};

/**
 * Chroma scale factors relative to the base chroma.
 * Lighter steps have less chroma saturation; midtones peak; darks muted.
 */
const CHROMA_CURVE: Record<string, number> = {
  '50': 0.15,
  '100': 0.30,
  '200': 0.55,
  '300': 0.75,
  '400': 0.90,
  '500': 1.00,
  '600': 0.95,
  '700': 0.85,
  '800': 0.70,
  '900': 0.55,
  '950': 0.40,
};

const STEPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;

export type ColorStep = (typeof STEPS)[number];
export type ColorScale = Record<ColorStep, string>;

/**
 * Generate a perceptually-even color scale from a base hex color.
 * The base hex maps to step 500. Other steps are derived by walking
 * lightness on a fixed curve while preserving hue and scaling chroma.
 */
export function generateColorScale(hex: string): ColorScale {
  const base = hexToOklch(hex);

  const scale = {} as ColorScale;

  for (const step of STEPS) {
    const targetL = LIGHTNESS_CURVE[step]!;
    const chromaFactor = CHROMA_CURVE[step]!;

    // For step 500, use the exact input color
    if (step === '500') {
      scale[step] = hex.toLowerCase();
      continue;
    }

    const color: OklchColor = {
      l: targetL,
      c: base.c * chromaFactor,
      h: base.h,
    };

    scale[step] = oklchToHex(color);
  }

  return scale;
}

// --------------------------------------------------------------------------
// Theme injection
// --------------------------------------------------------------------------

const STYLE_ELEMENT_ID = 'brand-theme';

/**
 * Build the CSS text for both dark (:root) and light (html.light) themes
 * from a primary and secondary color scale.
 */
function buildThemeCss(
  primaryScale: ColorScale,
  secondaryScale: ColorScale,
  primaryHex: string,
  secondaryHex: string,
): string {
  const darkVars = STEPS.map(
    (step) =>
      `  --color-accent-${step}: ${primaryScale[step]};`,
  ).join('\n');

  const darkSecondaryVars = STEPS.map(
    (step) =>
      `  --color-secondary-${step}: ${secondaryScale[step]};`,
  ).join('\n');

  // Subtle token for dark theme (base at 12% alpha)
  const darkSubtle = `  --color-accent-subtle: ${primaryHex}1f;`;
  const darkSecondarySubtle = `  --color-secondary-subtle: ${secondaryHex}1f;`;

  // For light theme, use the 600 step as the interactive/focus value
  // (mirrors current globals.css where light uses accent-600 for focus/sidebar)
  const lightVars = STEPS.map(
    (step) =>
      `  --color-accent-${step}: ${primaryScale[step]};`,
  ).join('\n');

  const lightSecondaryVars = STEPS.map(
    (step) =>
      `  --color-secondary-${step}: ${secondaryScale[step]};`,
  ).join('\n');

  const lightSubtle = `  --color-accent-subtle: ${primaryHex}14;`;
  const lightSecondarySubtle = `  --color-secondary-subtle: ${secondaryHex}14;`;

  // Also override border-focus and chart tokens that reference accent directly
  const darkExtra = [
    `  --color-border-focus: ${primaryScale['500']};`,
    `  --color-chart-1: ${primaryScale['500']};`,
    `  --color-chart-2: ${secondaryScale['500']};`,
  ].join('\n');

  const lightExtra = [
    `  --color-border-focus: ${primaryScale['600']};`,
    `  --color-chart-1: ${primaryScale['600']};`,
    `  --color-chart-2: ${secondaryScale['600']};`,
  ].join('\n');

  return `:root {\n${darkVars}\n${darkSubtle}\n${darkSecondaryVars}\n${darkSecondarySubtle}\n${darkExtra}\n}\n\nhtml.light {\n${lightVars}\n${lightSubtle}\n${lightSecondaryVars}\n${lightSecondarySubtle}\n${lightExtra}\n}\n`;
}

/**
 * Apply brand theme colors by creating/updating a <style id="brand-theme"> element.
 * Idempotent -- calling multiple times just updates the same element.
 *
 * @param primaryHex - Primary brand color as #rrggbb
 * @param secondaryHex - Secondary brand color as #rrggbb
 */
export function applyBrandTheme(primaryHex: string, secondaryHex: string): void {
  const primaryScale = generateColorScale(primaryHex);
  const secondaryScale = generateColorScale(secondaryHex);
  const css = buildThemeCss(primaryScale, secondaryScale, primaryHex, secondaryHex);

  let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;
}

// --------------------------------------------------------------------------
// Full branding orchestrator
// --------------------------------------------------------------------------

const BRANDING_STORAGE_KEY = 'opus-ia-branding';

/**
 * Full branding apply: theme colors + document title + favicon + localStorage cache.
 * Called by BrandingProvider on boot and by OrgSettingsCustomization on save.
 */
export function applyBranding(branding: BrandingPublicShape): void {
  // 1. Apply color scales
  applyBrandTheme(branding.primary_color, branding.secondary_color);

  // 2. Document title
  if (branding.platform_name) {
    document.title = branding.platform_name;
  }

  // 3. Favicon
  if (branding.logo_url) {
    let linkEl = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.rel = 'icon';
      document.head.appendChild(linkEl);
    }
    linkEl.href = branding.logo_url;
  }

  // 4. Persist to localStorage for FOUC prevention on next load
  try {
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // Storage full or restricted -- non-critical
  }
}

/**
 * Read cached branding from localStorage (used by FOUC script and initial hydration).
 */
export function getCachedBranding(): BrandingPublicShape | null {
  try {
    const raw = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as BrandingPublicShape;
  } catch {
    return null;
  }
}
